/**
 * THE frontend capability registry — the mirror of
 * `src/PartyResume.Api/Services/Staff/StaffCapabilities.cs`. The two must stay in
 * sync (a Jest snapshot of GET /api/staff/capabilities checks the key + verb set).
 *
 * A capability is one section of the admin panel. Verbs are view · create · edit ·
 * delete plus at most one EXTRA verb for the operation that is materially riskier
 * than an edit. `staff` is superadmin-only and can never be granted.
 *
 * Rows are in rail order: the capability grid and the rail are the same list.
 */
export const VIEW = 'view';
export const CREATE = 'create';
export const EDIT = 'edit';
export const DELETE = 'delete';
export const STAFF = 'staff';

const cap = (key, group, label, mark, verbs, extra = null, superadminOnly = false) => ({
  key, group, label, mark, verbs: extra ? [...verbs, extra] : verbs, extra, superadminOnly,
});

export const CAPABILITIES = [
  cap('stats',        'overview',  'STATS',          'ST', [VIEW]),
  cap('analytics',    'overview',  'ANALYTICS',      'AN', [VIEW]),
  cap('users',        'people',    'USERS',          'US', [VIEW, EDIT, DELETE]),
  cap('hosts',        'people',    'HOSTS',          'HO', [VIEW, EDIT], 'release'),
  cap('staff',        'people',    'STAFF',          'SF', [], null, true),
  cap('wallets',      'money',     'WALLETS',        'WA', [VIEW], 'adjust'),
  cap('transactions', 'money',     'TRANSACTIONS',   'TX', [VIEW]),
  cap('withdrawals',  'money',     'WITHDRAWALS',    'WD', [VIEW], 'approve'),
  cap('disputes',     'money',     'DISPUTES',       'DI', [VIEW], 'resolve'),
  cap('fees',         'money',     'BOOKING FEES',   'BF', [VIEW, EDIT]),
  cap('events',       'events',    'EVENTS',         'EV', [VIEW, EDIT], 'cancel'),
  cap('gatherings',   'events',    'GATHERINGS',     'GA', [VIEW], 'cancel'),
  cap('curation',     'events',    'CURATION',       'CU', [VIEW, EDIT]),
  cap('newsletter',   'marketing', 'NEWSLETTER',     'NL', [VIEW, CREATE, EDIT, DELETE], 'send'),
  cap('app_notices',  'marketing', 'APP NOTICES',    'NO', [VIEW, CREATE, EDIT, DELETE]),
  cap('brands',       'marketing', 'BRANDS',         'BR', [VIEW, CREATE, EDIT, DELETE]),
  cap('patrons',      'marketing', 'PARTY PATRONS',  'PP', [VIEW, CREATE, EDIT]),
  cap('reports',      'marketing', 'PATRON REPORTS', 'RP', [VIEW]),
  cap('party_engine', 'engines',   'PARTY ENGINE',   'PE', [VIEW, CREATE, EDIT], 'publish'),
  cap('listed_pros',  'engines',   'LISTED PROS',    'LP', [VIEW, CREATE, EDIT], 'publish'),
  cap('ingestion',    'engines',   'INGESTION',      'IN', [VIEW, CREATE, EDIT, DELETE], 'publish'),
  cap('pro_pages',    'engines',   'PRO PAGES',      'PR', [VIEW, EDIT, DELETE]),
  cap('planner',      'engines',   'PLANNER',        'PL', [VIEW, EDIT]),
  cap('plan_detection', 'engines', 'PLAN DETECTION', 'PD', [VIEW]),
  cap('settings',     'system',    'SETTINGS',       'SE', [VIEW, EDIT]),
  cap('regions',      'system',    'REGIONS',        'RG', [VIEW, CREATE, EDIT]),
  cap('squares',      'system',    'CITY PORTALS',   'CP', [VIEW, EDIT]),
  cap('activity',     'system',    'AUDIT LOG',      'AL', [VIEW], 'export'),
];

export const CAP_BY_KEY = Object.fromEntries(CAPABILITIES.map((c) => [c.key, c]));

/** Sections nobody can write: the view-only line is suppressed on them. */
export const READ_ONLY_SECTIONS = new Set(['stats', 'analytics', 'transactions', 'reports', 'activity', 'plan_detection']);

export const GROUPS = [
  { key: 'now',       label: 'NOW' },
  { key: 'overview',  label: 'OVERVIEW' },
  { key: 'people',    label: 'PEOPLE' },
  { key: 'money',     label: 'MONEY' },
  { key: 'events',    label: 'EVENTS' },
  { key: 'marketing', label: 'MARKETING' },
  { key: 'engines',   label: 'ENGINES' },
  { key: 'system',    label: 'SYSTEM' },
];

/**
 * The rail. One entry per section (not per capability: CURATION drives two rail
 * entries). `badge` names the counts field that fills the black
 * square; `dot` marks sections that carry the activity square instead.
 */
export const SECTIONS = [
  { id: 'today',        group: 'now',       label: 'TODAY',          mark: 'TD', cap: null },
  { id: 'stats',        group: 'overview',  label: 'STATS',          mark: 'ST', cap: 'stats' },
  { id: 'analytics',    group: 'overview',  label: 'ANALYTICS',      mark: 'AN', cap: 'analytics', dot: true },
  { id: 'users',        group: 'people',    label: 'USERS',          mark: 'US', cap: 'users' },
  { id: 'hosts',        group: 'people',    label: 'HOSTS',          mark: 'HO', cap: 'hosts' },
  { id: 'user-reports', group: 'people',    label: 'USER REPORTS',   mark: 'UR', cap: 'staff', badge: 'openUserReports' },
  { id: 'staff',        group: 'people',    label: 'STAFF',          mark: 'SF', cap: 'staff', badge: 'pendingInvites' },
  { id: 'wallets',      group: 'money',     label: 'WALLETS',        mark: 'WA', cap: 'wallets' },
  { id: 'transactions', group: 'money',     label: 'TRANSACTIONS',   mark: 'TX', cap: 'transactions' },
  { id: 'withdrawals',  group: 'money',     label: 'WITHDRAWALS',    mark: 'WD', cap: 'withdrawals', badge: 'pendingWithdrawals' },
  { id: 'disputes',     group: 'money',     label: 'DISPUTES',       mark: 'DS', cap: 'disputes', badge: 'openDisputes' },
  { id: 'fees',         group: 'money',     label: 'BOOKING FEES',   mark: 'FE', cap: 'fees' },
  { id: 'events',       group: 'events',    label: 'EVENTS',         mark: 'EV', cap: 'events' },
  { id: 'gatherings',   group: 'events',    label: 'GATHERINGS',     mark: 'GA', cap: 'gatherings' },
  { id: 'pick',         group: 'events',    label: 'PICK EVENTS',    mark: 'PK', cap: 'curation', geo: true },
  { id: 'featured',     group: 'events',    label: 'FEATURED',       mark: 'FT', cap: 'curation', geo: true },
  { id: 'newsletter',   group: 'marketing', label: 'NEWSLETTER',     mark: 'NL', cap: 'newsletter', dot: true },
  { id: 'notices',      group: 'marketing', label: 'APP NOTICES',    mark: 'AP', cap: 'app_notices' },
  { id: 'brands',       group: 'marketing', label: 'BRANDS',         mark: 'BR', cap: 'brands' },
  { id: 'patrons',      group: 'marketing', label: 'PARTY PATRONS',  mark: 'PP', cap: 'patrons', geo: true },
  { id: 'reports',      group: 'marketing', label: 'PATRON REPORTS', mark: 'RP', cap: 'reports' },
  { id: 'party-engine', group: 'engines',   label: 'PARTY ENGINE',   mark: 'PE', cap: 'party_engine', geo: true },
  { id: 'listed-pros',  group: 'engines',   label: 'LISTED PROS',    mark: 'LP', cap: 'listed_pros', badge: 'listedProClaims', geo: true },
  { id: 'ingestion',    group: 'engines',   label: 'INGESTION',      mark: 'IN', cap: 'ingestion', badge: 'ingestionQueue' },
  { id: 'pro-pages',    group: 'engines',   label: 'PRO PAGES',      mark: 'PR', cap: 'pro_pages' },
  { id: 'planner',      group: 'engines',   label: 'PLANNER',        mark: 'PL', cap: 'planner' },
  { id: 'plan-detection', group: 'engines', label: 'PLAN DETECTION', mark: 'PD', cap: 'plan_detection' },
  { id: 'settings',     group: 'system',    label: 'SETTINGS',       mark: 'FF', cap: 'settings' },
  { id: 'regions',      group: 'system',    label: 'REGIONS',        mark: 'RG', cap: 'regions' },
  { id: 'squares',      group: 'system',    label: 'CITY PORTALS',   mark: 'CP', cap: 'squares' },
  { id: 'audit',        group: 'system',    label: 'AUDIT LOG',      mark: 'AL', cap: 'activity', dot: true },
];

export const SECTION_BY_ID = Object.fromEntries(SECTIONS.map((s) => [s.id, s]));

/** Which sections a key opens. TODAY always; STAFF for superadmins; otherwise `view`. */
export function visibleSections(staff) {
  if (!staff?.isStaff || staff.status !== 'active') return [];
  return SECTIONS.filter((s) => {
    if (s.cap === null) return true;
    if (s.cap === STAFF) return !!staff.isSuperadmin;
    return can(staff, s.cap, VIEW);
  });
}

export function can(staff, capability, verb) {
  if (!staff?.isStaff || staff.status !== 'active') return false;
  if (staff.isSuperadmin) return true;
  if (capability === STAFF) return false;
  const verbs = staff.grants?.[capability];
  return Array.isArray(verbs) && verbs.includes(verb);
}

/** The person can view the section but holds none of its write verbs. */
export function isViewOnly(staff, capability) {
  if (!capability || READ_ONLY_SECTIONS.has(capability)) return false;
  const c = CAP_BY_KEY[capability];
  if (!c || !can(staff, capability, VIEW)) return false;
  return !c.verbs.some((v) => v !== VIEW && can(staff, capability, v));
}

/** Groups → visible sections, for the rail and the mobile sheet. Empty groups are dropped. */
export function railGroups(staff) {
  const visible = visibleSections(staff);
  return GROUPS.map((g) => ({ ...g, sections: visible.filter((s) => s.group === g.key) })).filter((g) => g.sections.length > 0);
}

/** Sections the person can open, excluding TODAY — the rail footer's number. */
export function sectionCount(staff) {
  return visibleSections(staff).filter((s) => s.cap !== null).length;
}

/** Applicable verbs for a grid row, in column order VIEW · CREATE · EDIT · DELETE · EXTRA. */
export const GRID_COLUMNS = [VIEW, CREATE, EDIT, DELETE];

export function normalizeGrants(grants) {
  const out = {};
  for (const c of CAPABILITIES) {
    if (c.superadminOnly) continue;
    const have = grants?.[c.key];
    if (!Array.isArray(have)) continue;
    const kept = c.verbs.filter((v) => have.includes(v));
    if (kept.length) out[c.key] = kept;
  }
  return out;
}

export function sameGrants(a, b) {
  const na = normalizeGrants(a); const nb = normalizeGrants(b);
  const ka = Object.keys(na); const kb = Object.keys(nb);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => nb[k] && na[k].length === nb[k].length && na[k].every((v) => nb[k].includes(v)));
}

/** "+ NEWSLETTER SEND · − BRANDS DELETE" — additions first, then removals. */
export function diffSummary(before, after) {
  const b = normalizeGrants(before); const a = normalizeGrants(after);
  const parts = [];
  for (const c of CAPABILITIES) {
    const was = b[c.key] || []; const now = a[c.key] || [];
    for (const v of c.verbs) if (now.includes(v) && !was.includes(v)) parts.push(`+ ${c.label} ${v.toUpperCase()}`);
  }
  for (const c of CAPABILITIES) {
    const was = b[c.key] || []; const now = a[c.key] || [];
    for (const v of c.verbs) if (was.includes(v) && !now.includes(v)) parts.push(`− ${c.label} ${v.toUpperCase()}`);
  }
  return parts.join(' · ');
}

/**
 * The same diff as `diffSummary`, as pills: `[{ sign: '+' | '−', label, added }]`.
 * Additions first, then removals — the STAFF sheets draw them green and red.
 */
export function diffPills(before, after) {
  const b = normalizeGrants(before); const a = normalizeGrants(after);
  const added = []; const removed = [];
  for (const c of CAPABILITIES) {
    const was = b[c.key] || []; const now = a[c.key] || [];
    for (const v of c.verbs) {
      if (now.includes(v) && !was.includes(v)) added.push({ sign: '+', added: true, label: `${c.label} ${v.toUpperCase()}` });
      else if (was.includes(v) && !now.includes(v)) removed.push({ sign: '−', added: false, label: `${c.label} ${v.toUpperCase()}` });
    }
  }
  return [...added, ...removed];
}

export function countGrants(grants) {
  const n = normalizeGrants(grants);
  return { sections: Object.keys(n).length, verbs: Object.values(n).reduce((s, v) => s + v.length, 0) };
}
